
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { MotionConfig } from 'framer-motion';
import { AppProps } from 'next/app';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import NProgress from 'nprogress'; //nprogress module
import { ReactNode, useEffect, useState } from 'react';
import { Toaster } from 'sonner';
// import 'nprogress/nprogress.css'; //styles of nprogress
// This is for demo purposes, remove @/styles/colors.css import immediately
import '@/styles/globals.css';
import { GTM_ID } from '@/lib/gtm';
import { AppShellDirection, AppShellDirectionContext, useDocumentDirection, } from '@/lib/textDirection';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import CookieConsent from '@/components/CookieConsent';
import LocaleInitializer from '@/components/layout/LocaleInitializer';
import SiteChatWidget from '@/components/SiteChatWidget';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AnalyticsProvider } from '@/contexts/Analytics';
import { setupChunkLoadErrorRecovery } from '@/utils/chunkLoadRecovery';
import { captureMarketingData } from '@/utils/marketingCapture';
import { MyUserContextProvider } from '@/utils/useUser';
// One React Query cache per browser tab, and a new one for every server
// render. A module-level client is shared by every SSR request the process
// serves: a query seeded with `initialData` then rendered whatever an earlier
// request had cached under the same key (another user's, for a key like
// ['headshot-models']) and mismatched on hydration.
let browserQueryClient: QueryClient | undefined;
function getQueryClient() {
    if (typeof window === 'undefined')
        return new QueryClient();
    browserQueryClient ??= new QueryClient();
    return browserQueryClient;
}
// Stable container resolver for all antd portals (Tooltip, Dropdown, Modal,
// notification, message, etc). MUST be defined at module scope — an inline
// `() => document.body` would create a new function on every render and
// defeat the bail-out in @rc-component/portal's setInnerContainer effect,
// reintroducing the React #185 "Maximum update depth" crash on Win/Edge.
// Repro: open /chat in Edge on Windows and start streaming a long response
// while a Tooltip-wrapped button is mounted. See https://react.dev/errors/185
const getBodyContainer = () => document.body;
// antd styles its components for RTL only when told to. Its own component, so
// a direction change re-renders antd's consumers, not the whole app.
function AntdConfig({ children }: {
    children: ReactNode;
}) {
    const direction = useDocumentDirection();
    return (<ConfigProvider getPopupContainer={getBodyContainer} direction={direction}>
      {children}
    </ConfigProvider>);
}
/** `appShellDirection` is passed by _document on the server only. */
type MyAppProps = AppProps & {
    appShellDirection?: AppShellDirection;
};
function MyApp(props: MyAppProps) {
    const router = useRouter();
    // Mobile login opens /turnstile in a fresh WebView. Mount only the security
    // widget: hiding the consent banner alone still allowed GTM, PostHog and
    // marketing capture to run. Keep the website's effects/providers in a
    // separate component so this branch never initializes them (including SSR).
    // /connect-done is an OAuth popup that closes itself right away.
    if (router.pathname === '/turnstile' || router.pathname === '/connect-done') {
        const { Component, pageProps } = props;
        return <Component {...pageProps}/>;
    }
    return (<AppShellDirectionContext.Provider value={props.appShellDirection ?? null}>
      {/* framer-motion drops movement and keeps fades for visitors whose
            system asks for reduced motion. */}
      <MotionConfig reducedMotion='user'>
        <WebsiteApp {...props}/>
      </MotionConfig>
    </AppShellDirectionContext.Provider>);
}
function WebsiteApp({ Component, pageProps }: AppProps) {
    const [queryClient] = useState(getQueryClient);
    useEffect(() => {
        captureMarketingData();
        const teardownChunkRecovery = setupChunkLoadErrorRecovery();
        // The progress bar shows only for navigations slow enough to notice: on a
        // fast one it would flash. No spinner, the bar already says it.
        NProgress.configure({ showSpinner: false });
        let progressTimer: number | undefined;
        const handleRouteChangeStart = () => {
            window.clearTimeout(progressTimer);
            progressTimer = window.setTimeout(() => NProgress.start(), 200);
        };
        const handleRouteChangeDone = () => {
            window.clearTimeout(progressTimer);
            NProgress.done();
        };
        Router.events.on('routeChangeStart', handleRouteChangeStart);
        Router.events.on('routeChangeComplete', handleRouteChangeDone);
        Router.events.on('routeChangeError', handleRouteChangeDone);
        return () => {
            window.clearTimeout(progressTimer);
            teardownChunkRecovery();
            Router.events.off('routeChangeStart', handleRouteChangeStart);
            Router.events.off('routeChangeComplete', handleRouteChangeDone);
            Router.events.off('routeChangeError', handleRouteChangeDone);
        };
    }, []);
    // Deferred Google Tag Manager script - loads after page becomes interactive
    const gtmScript = (<Script id='gtm' strategy='afterInteractive' dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');`,
        }}/>);
    if (pageProps?.isStaticPage) {
        return (<AppErrorBoundary>
        <AntdConfig>
          <TooltipProvider delayDuration={200}>
            {gtmScript}
            <AnalyticsProvider>
              <LocaleInitializer />
              <Component {...pageProps}/>
            </AnalyticsProvider>
            <SiteChatWidget />
          </TooltipProvider>
        </AntdConfig>
      </AppErrorBoundary>);
    }
    return (<AppErrorBoundary>
      <AntdConfig>
        <TooltipProvider delayDuration={200}>
          <MyUserContextProvider>
            <QueryClientProvider client={queryClient}>
              {/* Google Tag Manager - loads after page becomes interactive */}
              {gtmScript}

              <AnalyticsProvider>
                <LocaleInitializer />
                <Component {...pageProps}/>
              </AnalyticsProvider>
              <Toaster position='bottom-center' duration={5000} closeButton richColors theme='system'/>
            </QueryClientProvider>
            <CookieConsent />
            <SiteChatWidget />
          </MyUserContextProvider>
        </TooltipProvider>
      </AntdConfig>
    </AppErrorBoundary>);
}
const __Next_Translate__Page__1a1a5dd1c26__ = MyApp;

    export default __appWithI18n(__Next_Translate__Page__1a1a5dd1c26__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
    });
  